import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@components/button';
import { createForm } from '@components/form';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';
import { disabledSectionStyle } from '@styles/theme';

import { ModalProps } from 'features/modals';
import { useStore } from 'models/Provider';

import { TaktikalToken, TaktikalTokenError, taktikalSetToken, taktikalEditToken } from './api';

const Form = createForm<TaktikalToken>();

export const TaktikalConnectModal = (props: ModalProps<{ isEdit: boolean }>) => {
  const { t } = useStore();
  const { showError, showSuccess } = useToaster();
  const { closeModal, isEdit } = props;
  const client = useQueryClient();

  const submit = useMutation(
    (data: TaktikalToken) => (isEdit ? taktikalEditToken(data) : taktikalSetToken(data)),
    {
      onSuccess: () => {
        showSuccess(t('integrations.taktikal.settings.connect.modal.saved'));
        client.resetQueries(['taktikal-token']);
        closeModal();
      },
      onError: (data: TaktikalTokenError) => {
        if (data && data.response && data.response.data && data.response.data.message) {
          showError(t(data.response.data.message));
        } else {
          showError(t('integrations.taktikal.settings.connect.modal.error'));
        }
      },
    },
  );

  return (
    <Stack space={6} style={submit.isLoading ? disabledSectionStyle : undefined}>
      <Text font="heading2" text={t('integrations.taktikal.settings.connect.modal.header')} />
      <Text font="regular16" text={t('integrations.taktikal.settings.connect.modal.description')} />

      <Form onSubmit={(values) => submit.mutateAsync(values)}>
        <Form.TextField
          label={t('integrations.taktikal.settings.connect.modal.flowkey.label')}
          id="flowKey"
          validators={['required']}
        />
        <Form.TextField
          label={t('integrations.taktikal.settings.connect.modal.apikey.label')}
          id="apiKey"
          validators={['required']}
        />
        <Form.TextField
          label={t('integrations.taktikal.settings.connect.modal.companykey.label')}
          id="companyKey"
          validators={['required']}
        />
        <Form.Actions>
          <Button
            variant="secondary"
            text={t('integrations.taktikal.settings.connect.modal.cancel')}
            onClick={closeModal}
          />
          <Form.Submit text={t('integrations.taktikal.settings.connect.modal.connect')} />
        </Form.Actions>
      </Form>
    </Stack>
  );
};
